import { useTranslation } from 'react-i18next';

import type { CadeSourceSessions, YearWindow } from '@/services/types';

/**
 * The years a group of figures counts over, stated once beneath the group.
 *
 * Every CADE card and panel has to answer "from when till when", and the
 * answer is the same for every figure drawn from the session archive -- so it
 * is printed once per group rather than once per figure, from the SAME
 * `year_window` the strip renders, through the same locale keys. A group whose
 * payload carries no window renders nothing rather than a guess: the absence
 * of this line is itself the statement that no window could be measured.
 *
 * `data-year-window` so a test can find the caption without matching its prose.
 */
export function WindowCaption({ window }: { window: YearWindow | null | undefined }) {
  const { t } = useTranslation('common');
  if (!window) return null;
  return (
    <p className="text-xs text-muted-foreground" data-year-window>
      <span className="font-medium">{t('asOf.coverage')}:</span>{' '}
      <span className="tabular-nums">
        {t('asOf.yearWindow', { first: window.first, last: window.last })}
      </span>{' '}
      ({t(`asOf.basis.${window.basis}`, { defaultValue: window.basis })})
    </p>
  );
}


/**
 * Where the archive's atas come from, when it joins more than one source: the atas CADE
 * publishes on its portal and, for the years it never published, atas rebuilt from their copies
 * inside SEI. Counted per request; one source draws nothing.
 */
export function ArchiveSources({ sources }: { sources: CadeSourceSessions[] | undefined }) {
  const { t } = useTranslation('cade');
  if (!sources || sources.length < 2) return null;
  return (
    <p className="text-xs text-muted-foreground" data-archive-sources>
      {sources
        .map((s) =>
          t(`archiveSources.${s.kind}`, {
            sessions: s.sessions,
            decisions: s.decisions,
            first: s.first_year ?? '—',
            last: s.last_year ?? '—',
            defaultValue: s.kind,
          }),
        )
        .join(' ')}
    </p>
  );
}
